import { useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import CardMagasinhome from "./components/homePage/cardMagasinhome";
import HomeService from "./services/PosService/HomeService";
import SocieteAndUserService from "./services/societeAndUser";
import getCurrentUserId from "./reduxSaga/sagas/requests/currentUserId";
import withLoader from "./components/layouts/WithLoader";
import KPISaleService from "./services/KPISaleService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const MONTHS_FR = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const fmt3 = (v) => v != null ? parseFloat(Number(v).toFixed(3)) : 0;

// ── helpers ──────────────────────────────────────────────────────────────────

const byIdMonth = (data, field) => {
  const arr = Array(12).fill(0);
  (data || []).forEach((item) => {
    const m = item?.id?.month;
    if (m >= 1 && m <= 12) arr[m - 1] = fmt3(item[field]);
  });
  return arr;
};

const byMonth = (data, field) => {
  const arr = Array(12).fill(0);
  (data || []).forEach((item) => {
    const m = item?.month;
    if (m >= 1 && m <= 12) arr[m - 1] = fmt3(item[field]);
  });
  return arr;
};

/**
 * Linear-regression prevision.
 * For each month with value === 0 (no data), predict using the trend
 * computed on months that DO have data.
 * Returns an array of 12 values: prevision for empty months, 0 for actual months.
 */
const computePrevision = (arr) => {
  const points = [];
  arr.forEach((v, i) => { if (v > 0) points.push({ x: i + 1, y: v }); });

  if (points.length < 2) {
    // not enough data → flat average of what exists
    if (points.length === 1) {
      const avg = points[0].y;
      return arr.map((v) => (v > 0 ? 0 : fmt3(avg)));
    }
    return Array(12).fill(0);
  }

  const n     = points.length;
  const sumX  = points.reduce((s, p) => s + p.x, 0);
  const sumY  = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;

  if (denom === 0) {
    const avg = sumY / n;
    return arr.map((v) => (v > 0 ? 0 : fmt3(avg)));
  }

  const slope     = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const predict   = (x) => fmt3(Math.max(0, slope * x + intercept));

  return arr.map((v, i) => (v > 0 ? 0 : predict(i + 1)));
};

/**
 * Build a comparative bar-chart dataset:
 *  - dataset 0 (year N): actual months solid, prevision months hatched/lighter
 *  - dataset 1 (year N-1): all semi-transparent
 */
const buildChart = (curr, curr_p, yearN, yearP, colorSolid, colorPrev, colorOld) => {
  const prevision = computePrevision(curr);
  const merged    = curr.map((v, i) => (v > 0 ? v : prevision[i]));
  const bgColors  = curr.map((v, i) => (v > 0 ? colorSolid : colorPrev));
  const bdColors  = curr.map((v, i) => (v > 0 ? colorSolid : colorSolid.replace(/[\d.]+\)$/, "1)")));

  return {
    labels: MONTHS_FR,
    datasets: [
      {
        label: `${yearN} (prévision = barres claires)`,
        data: merged,
        backgroundColor: bgColors,
        borderColor: bdColors,
        borderWidth: 1,
      },
      {
        label: `${yearP}`,
        data: curr_p,
        backgroundColor: colorOld,
        borderWidth: 0,
      },
    ],
  };
};

// ── KPI evolution badge ───────────────────────────────────────────────────────

const evolution = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const EvoBadge = ({ curr, prev }) => {
  const pct = evolution(curr, prev);
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <small style={{ fontSize: "0.75rem", color: up ? "#4caf50" : "#f44336", fontWeight: 600 }}>
      <i className={`fas fa-arrow-${up ? "up" : "down"} mr-1`}></i>
      {Math.abs(pct).toFixed(1)}%
    </small>
  );
};

// ── component ─────────────────────────────────────────────────────────────────

const HomePage = ({ startLoading, stopLoading }) => {
  const params = useParams();
  const [openInfoAlert,  setOpenInfoAlert]  = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState("");
  const [userRole,  setUserRole]  = useState("");
  const [shopList,  setShopList]  = useState([]);
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [dashLoading, setDashLoading] = useState(false);

  // current year
  const [factureCA,    setFactureCA]    = useState([]);
  const [bdcCA,        setBdcCA]        = useState([]);
  const [facturePayed, setFacturePayed] = useState([]);
  const [devisCounts,  setDevisCounts]  = useState([]);
  const [bdcCounts,    setBdcCounts]    = useState([]);

  // previous year
  const [factureCA_p,    setFactureCA_p]    = useState([]);
  const [bdcCA_p,        setBdcCA_p]        = useState([]);
  const [facturePayed_p, setFacturePayed_p] = useState([]);
  const [devisCounts_p,  setDevisCounts_p]  = useState([]);
  const [bdcCounts_p,    setBdcCounts_p]    = useState([]);

  const card = [
    { icon: "fas fa-hand-holding-dollar", titre: "chiffre d'affaire", by: "Jour" },
    { icon: "fas fa-hand-holding-dollar", titre: "chiffre d'affaire", by: "Semaine" },
    { icon: "fas fa-hand-holding-dollar", titre: "chiffre d'affaire", by: "Mois" },
    { icon: "fas fa-hand-holding-dollar", titre: "chiffre d'affaire", by: "Année" },
  ];

  const handleInfoClose  = (e, r) => { if (r !== "clickaway") setOpenInfoAlert(false); };
  const handleEroorClose = (e, r) => { if (r !== "clickaway") setOpenErrorAlert(false); };

  const fetchDashboard = (y) => {
    const uid = getCurrentUserId();
    const yp  = y - 1;
    setDashLoading(true);
    startLoading();
    Promise.all([
      KPISaleService.getToByYear(uid, y),
      KPISaleService.getBDCToByYear(uid, y),
      KPISaleService.numberFacturePayed(uid, y),
      KPISaleService.numberDevisMonthsInYear(uid, y),
      KPISaleService.numberBDCMonthsInYear(uid, y),
      KPISaleService.getToByYear(uid, yp),
      KPISaleService.getBDCToByYear(uid, yp),
      KPISaleService.numberFacturePayed(uid, yp),
      KPISaleService.numberDevisMonthsInYear(uid, yp),
      KPISaleService.numberBDCMonthsInYear(uid, yp),
    ])
      .then(([r1,r2,r3,r4,r5, p1,p2,p3,p4,p5]) => {
        setFactureCA(r1?.data?.success    || r1?.data || []);
        setBdcCA(r2?.data?.success        || r2?.data || []);
        setFacturePayed(r3?.data?.success  || r3?.data || []);
        setDevisCounts(r4?.data?.success   || r4?.data || []);
        setBdcCounts(r5?.data?.success     || r5?.data || []);

        setFactureCA_p(p1?.data?.success    || p1?.data || []);
        setBdcCA_p(p2?.data?.success        || p2?.data || []);
        setFacturePayed_p(p3?.data?.success  || p3?.data || []);
        setDevisCounts_p(p4?.data?.success   || p4?.data || []);
        setBdcCounts_p(p5?.data?.success     || p5?.data || []);
      })
      .catch(() => {
        setOpenErrorAlert(true);
        setErrorAlertText("Erreur : problème de connexion dashboard");
      })
      .finally(() => {
        setDashLoading(false);
        stopLoading();
      });
  };

  useEffect(() => { if (params.isAccessDenied === "true") setOpenInfoAlert(true); }, []);

  useEffect(() => {
    const status = localStorage.getItem("status");
    setUserRole(status === null ? ["notConnected"] : JSON.parse(status));
  }, []);

  useEffect(() => {
    if (userRole.includes("ADMIN")) getSocieteId();
    else if (userRole.includes("DIRECTEUR_MAGASIN")) getShopAndChiffreAffaireStatByUser();
    // KPI sales dashboard only for the ADMIN role (SUPER_ADMIN gets other statistics)
    if (userRole.includes("ADMIN")) fetchDashboard(year);
  }, [userRole]);

  const getShopAndChiffreAffaireStatByUser = () => {
    startLoading();
    HomeService.getShopAndChiffreAffaireStatByUser(getCurrentUserId())
      .then((r) => {
        if (r?.data?.success) setShopList(r.data.success);
        else if (r?.data?.error) { setOpenErrorAlert(true); setErrorAlertText("error : " + r.data.error); }
      })
      .catch((e) => { setOpenErrorAlert(true); setErrorAlertText("error : " + e); })
      .finally(() => stopLoading());
  };

  const getSocieteId = () => {
    startLoading();
    SocieteAndUserService.getSocieteByIdUser(getCurrentUserId())
      .then((r) => {
        if (r?.data?.Societe) {
          getShopAndChiffreAffaireStatBySociete(r.data.Societe.id);
          localStorage.setItem("fileUrl", r.data.Societe.fileUrl);
        } else if (r?.data?.error) { setOpenErrorAlert(true); setErrorAlertText("error : " + r.data.error); }
      })
      .catch((e) => { setOpenErrorAlert(true); setErrorAlertText("error : " + e); })
      .finally(() => stopLoading());
  };

  const getShopAndChiffreAffaireStatBySociete = (idSociete) => {
    startLoading();
    HomeService.getShopAndChiffreAffaireStatBySociete(idSociete)
      .then((r) => {
        if (r?.data?.success) setShopList(r.data.success);
        else if (r?.data?.error) { setOpenErrorAlert(true); setErrorAlertText("error : " + r.data.error); }
      })
      .catch((e) => { setOpenErrorAlert(true); setErrorAlertText("error : " + e); })
      .finally(() => stopLoading());
  };

  // ── computed — year N ──
  const factureHT = byIdMonth(factureCA, "totalAmountSellingPrice");
  const factureTT = byIdMonth(factureCA, "totalAmountPriceTT");
  const bdcHT     = byIdMonth(bdcCA,    "totalAmountSellingPrice");
  const bdcTT     = byIdMonth(bdcCA,    "totalAmountPriceTT");
  const devisArr  = byMonth(devisCounts, "countAll");
  const bdcArr    = byMonth(bdcCounts,   "countAll");

  // ── computed — year N-1 ──
  const factureHT_p = byIdMonth(factureCA_p, "totalAmountSellingPrice");
  const factureTT_p = byIdMonth(factureCA_p, "totalAmountPriceTT");
  const bdcHT_p     = byIdMonth(bdcCA_p,    "totalAmountSellingPrice");
  const bdcTT_p     = byIdMonth(bdcCA_p,    "totalAmountPriceTT");
  const devisArr_p  = byMonth(devisCounts_p, "countAll");
  const bdcArr_p    = byMonth(bdcCounts_p,   "countAll");

  // ── previsions (linear regression on empty months of year N) ──
  const prevFactureHT = computePrevision(factureHT);
  const prevFactureTT = computePrevision(factureTT);
  const prevBdcHT     = computePrevision(bdcHT);
  const prevBdcTT     = computePrevision(bdcTT);
  const prevDevis     = computePrevision(devisArr);
  const prevBdc       = computePrevision(bdcArr);

  // ── totals (actual + prevision) for KPI cards ──
  const mergedSum = (actual, prev) =>
    actual.map((v, i) => (v > 0 ? v : prev[i])).reduce((a, b) => a + b, 0);

  const totalFactureHT   = mergedSum(factureHT, prevFactureHT);
  const totalFactureTT   = mergedSum(factureTT, prevFactureTT);
  const totalBdcHT       = mergedSum(bdcHT, prevBdcHT);
  const totalDevis       = mergedSum(devisArr, prevDevis);
  const totalBdc         = mergedSum(bdcArr, prevBdc);
  const payedItem        = Array.isArray(facturePayed) ? facturePayed.find(f => f?.id === 1) : null;
  const totalPayed       = payedItem?.count || 0;

  // prev year totals
  const totalFactureHT_p = factureHT_p.reduce((a, b) => a + b, 0);
  const totalFactureTT_p = factureTT_p.reduce((a, b) => a + b, 0);
  const totalBdcHT_p     = bdcHT_p.reduce((a, b) => a + b, 0);
  const totalDevis_p     = devisArr_p.reduce((a, b) => a + b, 0);
  const totalBdc_p       = bdcArr_p.reduce((a, b) => a + b, 0);
  const payedItem_p      = Array.isArray(facturePayed_p) ? facturePayed_p.find(f => f?.id === 1) : null;
  const totalPayed_p     = payedItem_p?.count || 0;

  const prevYear = year - 1;

  // ── build chart data ──
  const barFactureHT  = buildChart(factureHT,  factureHT_p,  year, prevYear, "rgba(30,136,229,0.85)",  "rgba(30,136,229,0.25)",  "rgba(30,136,229,0.3)");
  const barFactureTT  = buildChart(factureTT,  factureTT_p,  year, prevYear, "rgba(0,188,212,0.85)",   "rgba(0,188,212,0.25)",   "rgba(0,188,212,0.3)");
  const barBdcHT      = buildChart(bdcHT,      bdcHT_p,      year, prevYear, "rgba(255,152,0,0.85)",   "rgba(255,152,0,0.25)",   "rgba(255,152,0,0.3)");
  const barBdcTT      = buildChart(bdcTT,      bdcTT_p,      year, prevYear, "rgba(255,87,34,0.85)",   "rgba(255,87,34,0.25)",   "rgba(255,87,34,0.3)");
  const barDevis      = buildChart(devisArr,   devisArr_p,   year, prevYear, "rgba(76,175,80,0.85)",   "rgba(76,175,80,0.25)",   "rgba(76,175,80,0.3)");
  const barBdcCount   = buildChart(bdcArr,     bdcArr_p,     year, prevYear, "rgba(156,39,176,0.85)",  "rgba(156,39,176,0.25)",  "rgba(156,39,176,0.3)");

  const doughnutFacture = {
    labels: ["CA HT", "TVA"],
    datasets: [{
      data: [fmt3(totalFactureHT), fmt3(totalFactureTT - totalFactureHT)],
      backgroundColor: ["#1e88e5", "#e53935"],
    }],
  };

  const chartOpts = (title) => ({
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: !!title, text: title, font: { size: 12 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const isActual = ctx.datasetIndex === 0;
            const isPrev   = isActual && ctx.raw > 0;
            const label    = ctx.dataset.label?.split(" (")[0] || "";
            if (isActual) {
              // detect if this bar is a prevision (lighter color)
              const rawBg = Array.isArray(ctx.dataset.backgroundColor)
                ? ctx.dataset.backgroundColor[ctx.dataIndex]
                : ctx.dataset.backgroundColor;
              const isPrevi = rawBg && rawBg.includes("0.25");
              return `${isPrevi ? "📊 Prévision" : "✅ Réel"} ${label} : ${fmt3(ctx.raw)}`;
            }
            return `${label} : ${fmt3(ctx.raw)}`;
          },
        },
      },
    },
    scales: { y: { beginAtZero: true } },
  });

  const kpiCards = [
    { icon: "fas fa-file-invoice-dollar", bg: "bg-dark",             label: "CA Factures HT", curr: totalFactureHT, prev: totalFactureHT_p },
    { icon: "fas fa-dollar-sign",         bg: "bg-gradient-info",    label: "CA Factures TT", curr: totalFactureTT, prev: totalFactureTT_p },
    { icon: "fas fa-shopping-cart",       bg: "bg-dark",             label: "CA BDC HT",      curr: totalBdcHT,     prev: totalBdcHT_p     },
    { icon: "fas fa-check-circle",        bg: "bg-gradient-success", label: "Factures payées",curr: totalPayed,     prev: totalPayed_p     },
    { icon: "fas fa-file-alt",            bg: "bg-dark",             label: "Nb Devis",       curr: totalDevis,     prev: totalDevis_p     },
    { icon: "fas fa-clipboard-list",      bg: "bg-gradient-warning", label: "Nb BDC",         curr: totalBdc,       prev: totalBdc_p       },
  ];

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <br/>
      {/* <div className="content">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-12 mt-3">
              <CardMagasinhome shopList={shopList} card={card} />
            </div>
          </div>
        </div>
      </div> */}

      {/* ── Dashboard ── */}
      {userRole.includes("ADMIN") && (
      <div className="content">
        <div className="container-fluid">

          {/* Header */}
          <div className="card card-dark mb-3">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-chart-bar mr-2"></i>
                Dashboard Ventes &nbsp;
                <span className="badge badge-light">{year}</span>
                <span className="text-muted mx-2" style={{ fontSize: "0.85rem" }}>vs</span>
                <span className="badge badge-secondary">{prevYear}</span>
              </h3>
              <div className="card-tools d-flex align-items-center">
                <span className="badge badge-warning mr-3" style={{ fontSize: "0.75rem" }}>
                  <i className="fas fa-magic mr-1"></i> Barres claires = prévision par régression
                </span>
                <input
                  type="number"
                  className="form-control form-control-sm mr-2"
                  style={{ width: 100 }}
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                />
                <button className="btn btn-sm btn-outline-light" onClick={() => fetchDashboard(year)}>
                  <i className="fas fa-sync-alt mr-1"></i> Actualiser
                </button>
              </div>
            </div>
          </div>

          {dashLoading ? (
            <div style={{ minHeight: 300 }} />
          ) : (
            <>
              {/* ── KPI cards ── */}
              <div className="row">
                {kpiCards.map((k, i) => (
                  <div className="col" key={i}>
                    <div className={`info-box ${k.bg}`}>
                      <span className="info-box-icon"><i className={k.icon}></i></span>
                      <div className="info-box-content">
                        <span className="info-box-text">{k.label}</span>
                        <span className="info-box-number">{fmt3(k.curr)}</span>
                        <div className="progress"><div className="progress-bar" style={{ width: "100%" }} /></div>
                        <span className="progress-description d-flex align-items-center" style={{ gap: 6 }}>
                          <span style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                            {prevYear} : {fmt3(k.prev)}
                          </span>
                          <EvoBadge curr={k.curr} prev={k.prev} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── CA Factures ── */}
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">CA Factures H.T</h3></div>
                    <div className="card-body"><Bar data={barFactureHT} options={chartOpts("")} /></div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">CA Factures T.T</h3></div>
                    <div className="card-body"><Bar data={barFactureTT} options={chartOpts("")} /></div>
                  </div>
                </div>
              </div>

              {/* ── Doughnut ── */}
              <div className="row mt-3">
                <div className="col-md-4 offset-md-4">
                  <div className="card card-dark">
                    <div className="card-header">
                      <h3 className="card-title">Répartition HT / TVA ({year} — avec prévisions)</h3>
                    </div>
                    <div className="card-body">
                      <Doughnut data={doughnutFacture} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CA BDC ── */}
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">CA BDC H.T</h3></div>
                    <div className="card-body"><Bar data={barBdcHT} options={chartOpts("")} /></div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">CA BDC T.T</h3></div>
                    <div className="card-body"><Bar data={barBdcTT} options={chartOpts("")} /></div>
                  </div>
                </div>
              </div>

              {/* ── Nb Devis & BDC ── */}
              <div className="row mt-3 mb-4">
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">Nombre de Devis / mois</h3></div>
                    <div className="card-body"><Bar data={barDevis} options={chartOpts("")} /></div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header"><h3 className="card-title">Nombre de BDC / mois</h3></div>
                    <div className="card-body"><Bar data={barBdcCount} options={chartOpts("")} /></div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      <Snackbar open={openInfoAlert} autoHideDuration={2000} onClose={handleInfoClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert variant="filled" onClose={handleInfoClose} severity="info">Votre session a expiré</Alert>
      </Snackbar>
      <Snackbar open={openErrorAlert} autoHideDuration={3000} onClose={handleEroorClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert onClose={handleEroorClose} severity="error">{errorAlertText}</Alert>
      </Snackbar>
    </div>
  );
};
export default withLoader(HomePage);
